import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { getAgentConfigDisplayName } from "../../../utils/render";
import AssetPicker from "./AssetPicker";
import UserAccessControlEditor from "./UserAccessControlEditor";
import FeatureInsightsConfigEditor, {
  FeatureInsightsConfig,
} from "./FeatureInsightsConfigEditor";

interface EditLinkDialogProps {
  open: boolean;
  onClose: () => void;
  link: any;
  onSave: (updatedLink: any) => void;
  organizationId: string;
  linkType?: "assessment" | "roleplay"; // Determines which fields to show
}

const EditLinkDialog: React.FC<EditLinkDialogProps> = ({
  open,
  onClose,
  link,
  onSave,
  organizationId,
  linkType = "assessment", // Default to assessment for backward compatibility
}) => {
  const { agentConfigs } = useSelector(
    (state: RootState) => state.agentConfigs
  );

  const [selectedAgentConfigId, setSelectedAgentConfigId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const [testOverrideHumanDescription, setTestOverrideHumanDescription] =
    useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [selectedNamespaceAssetRegistry, setSelectedNamespaceAssetRegistry] =
    useState<{
      namespace_assets: Record<string, string[]>;
    } | null>(null);

  // Access control states
  const [userAccessControlEnabled, setUserAccessControlEnabled] =
    useState(false);
  const [maxSessionsEnabled, setMaxSessionsEnabled] = useState(false);
  const [maxSessionsPerUser, setMaxSessionsPerUser] = useState<number>(1);

  const [emailsEnabled, setEmailsEnabled] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [domainsEnabled, setDomainsEnabled] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);

  // Feature Insights Config state
  const [insightsConfig, setInsightsConfig] =
    useState<FeatureInsightsConfig | null>(null);

  // Control graph key state
  const [selectedControlGraphKey, setSelectedControlGraphKey] = useState<
    string | null
  >(null);

  const normalizeDomain = (d: string) => {
    const trimmed = (d || "").trim();
    return trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
  };

  // Initialize form values when link changes or dialog opens
  useEffect(() => {
    if (link && open) {
      setSelectedAgentConfigId(link.agent_config_id || "");
      const linkDisplayName = link.display_name || "";
      setDisplayName(linkDisplayName);
      // If display_name is empty when opening, mark as touched to show validation
      setDisplayNameTouched(linkDisplayName.trim() === "");
      setTestOverrideHumanDescription(
        link.test_override_human_description || ""
      );
      setAdditionalContext(link.additional_context || "");
      setSelectedNamespaceAssetRegistry(link.namespace_asset_registry || null);

      // ACL settings
      const policy = link.consumption_policy ?? null;
      const hasPolicy = !!policy;
      setUserAccessControlEnabled(hasPolicy);

      setMaxSessionsEnabled(!!policy?.max_num_converted_sessions_per_user);
      setMaxSessionsPerUser(policy?.max_num_converted_sessions_per_user || 1);

      const emailAllow: string[] | null = policy?.email_allowlist ?? null;
      setEmailsEnabled(Array.isArray(emailAllow) && emailAllow.length > 0);
      setEmails(emailAllow ?? []);

      const domainAllow: string[] | null =
        policy?.email_domains_allowlist ?? null;
      setDomainsEnabled(Array.isArray(domainAllow) && domainAllow.length > 0);
      setDomains(domainAllow ?? []);

      // Initialize insights config
      setInsightsConfig(link.insights_config || null);

      // Initialize control graph key
      setSelectedControlGraphKey(link.control_graph_key || null);
    }
  }, [link, open]);

  const handleSave = () => {
    const updatedLink: any = {
      id: link.id,
      organization_id: organizationId,
      agent_config_id: selectedAgentConfigId,
      display_name: displayName || null,
      test_override_human_description: testOverrideHumanDescription || null,
      additional_context: additionalContext || null,
    };

    // Only include assessment-specific fields for assessment links
    if (linkType === "assessment") {
      let consumptionPolicy: Record<string, any> | null = null;

      if (userAccessControlEnabled) {
        consumptionPolicy = {};

        if (maxSessionsEnabled) {
          const n = Math.max(1, Math.floor(maxSessionsPerUser || 1));
          consumptionPolicy.max_num_converted_sessions_per_user = n;
        }

        if (emailsEnabled) {
          const cleanedEmails = (emails || [])
            .map((e) => e.trim())
            .filter(Boolean);
          if (cleanedEmails.length > 0) {
            consumptionPolicy.email_allowlist = cleanedEmails;
          }
        }

        if (domainsEnabled) {
          const cleanedDomains = (domains || [])
            .map(normalizeDomain)
            .map((d) => d.trim())
            .filter(Boolean);
          if (cleanedDomains.length > 0) {
            consumptionPolicy.email_domains_allowlist = cleanedDomains;
          }
        }

        if (Object.keys(consumptionPolicy).length === 0) {
          consumptionPolicy = null;
        }
      }

      updatedLink.namespace_asset_registry = selectedNamespaceAssetRegistry;
      updatedLink.consumption_policy = consumptionPolicy;
      updatedLink.insights_config = insightsConfig;
      updatedLink.control_graph_key = selectedControlGraphKey;
    }

    onSave(updatedLink);
  };

  const handleClose = () => {
    // Reset form state
    setSelectedAgentConfigId("");
    setDisplayName("");
    setDisplayNameTouched(false);
    setTestOverrideHumanDescription("");
    setAdditionalContext("");
    setSelectedNamespaceAssetRegistry(null);
    setUserAccessControlEnabled(false);
    setMaxSessionsEnabled(false);
    setMaxSessionsPerUser(1);
    setEmailsEnabled(false);
    setEmails([]);
    setDomainsEnabled(false);
    setDomains([]);
    setInsightsConfig(null);
    setSelectedControlGraphKey(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>Edit Link</DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Box sx={{ pt: 2, pb: 2 }}>
          <Autocomplete
            options={agentConfigs.filter(
              (config) => config.organization_id === organizationId
            )}
            getOptionLabel={(option) =>
              getAgentConfigDisplayName({
                display_name: option.display_name || null,
                bot_name: option.bot_name || null,
                id: option.id ?? "",
              })
            }
            value={
              agentConfigs.find((config) => config.id === selectedAgentConfigId) ||
              null
            }
            onChange={(_, newValue) => {
              setSelectedAgentConfigId(newValue?.id || "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agent Configuration"
                required
                helperText="Search by display name, bot name, or ID"
              />
            )}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Link Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onBlur={() => setDisplayNameTouched(true)}
            error={displayNameTouched && displayName.trim() === ""}
            helperText={
              displayNameTouched && displayName.trim() === ""
                ? "Link Display Name cannot be empty. Give this link a friendly name (e.g., 'Q1 Sales Training Link')"
                : "Give this link a friendly name (e.g., 'Q1 Sales Training Link')"
            }
            sx={{ mb: 3 }}
            placeholder="e.g., Q1 Sales Training Link"
            required
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Test Override Fields (Optional)
          </Typography>

          <TextField
            fullWidth
            label="Override Human Description"
            value={testOverrideHumanDescription}
            onChange={(e) => setTestOverrideHumanDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            helperText="Override the human speaker's description for testing purposes"
          />

          <TextField
            fullWidth
            label="Additional Context"
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 3 }}
            helperText="Add additional context for the assessment"
          />

          {/* Assessment-only fields */}
          {/* Control Graph Key - available for both assessment and roleplay */}
          <TextField
            fullWidth
            label="Control Graph Key (Optional)"
            value={selectedControlGraphKey || ""}
            onChange={(e) => setSelectedControlGraphKey(e.target.value || null)}
            sx={{ mb: 3 }}
            helperText={
              linkType === "roleplay"
                ? "Specify a control graph resource key (e.g., 'control_graph:example_key') to override the roleplay's default conversation flow"
                : "Specify a control graph resource key (e.g., 'control_graph:ortho_intake') to override the assessment's default conversation flow"
            }
            placeholder="control_graph:example_key"
          />

          {linkType === "assessment" && (
            <>
              <UserAccessControlEditor
                enabled={userAccessControlEnabled}
                onToggleEnabled={setUserAccessControlEnabled}
                maxSessionsEnabled={maxSessionsEnabled}
                onToggleMaxSessions={setMaxSessionsEnabled}
                maxSessionsPerUser={maxSessionsPerUser}
                onMaxSessionsChange={setMaxSessionsPerUser}
                emailsEnabled={emailsEnabled}
                emails={emails}
                onToggleEmailsEnabled={setEmailsEnabled}
                onEmailsChange={setEmails}
                domainsEnabled={domainsEnabled}
                domains={domains}
                onToggleDomainsEnabled={setDomainsEnabled}
                onDomainsChange={setDomains}
              />

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Assets (Optional)
              </Typography>

              <AssetPicker
                namespace={`${organizationId}:assessments`}
                selectedNamespaceAssetRegistry={selectedNamespaceAssetRegistry}
                onSelectionChange={setSelectedNamespaceAssetRegistry}
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Feature Insights Configuration (Optional)
              </Typography>

              <FeatureInsightsConfigEditor
                value={insightsConfig}
                onChange={setInsightsConfig}
                organizationId={organizationId}
              />
            </>
          )}

          {/* Roleplay-only message */}
          {linkType === "roleplay" && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
              <Typography variant="body2" color="info.dark">
                Note: Roleplay links support basic configuration and control graphs.
                Advanced features like access control, assets, and insights are
                not yet available for roleplay links.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedAgentConfigId || displayName.trim() === ""}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLinkDialog;
